import { db } from '../firebase/firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { comparePlans, PlanComparisonResult } from './planComparisonService';
import { updateFamilyMetadata } from '../utils/updateFamilyMetadata'; // ⭐ FASE 2

/**
 * @fileoverview Servicio para manejar downgrade de plan
 * Pausa contenido, crea snapshots, y restaura al renovar
 */

export interface DowngradeSelections {
  activeGroupId?: string;
  activeFamilyIds?: string[]; // Para cuando hay múltiples grupos
  activeMemberIds?: string[];
  activeActivityIds?: string[];
  activeChecklistIds?: string[];
  activeRecipeIds?: string[];
  activeShoppingListId?: string;
}

export interface DowngradeSnapshot {
  id?: string;
  userId: string;
  familyId: string;
  
  // Plan anterior
  previousPlan: {
    id: string;
    name: string;
    expiresAt: Timestamp;
  };
  
  // Selecciones del usuario
  selections: DowngradeSelections;
  
  // Snapshot de contenido pausado
  pausedContent: {
    groups: Array<{ id: string; name: string }>;
    members: Array<{ id: string; name: string }>;
    activities: Array<{ id: string; name: string }>;
    checklists: Array<{ id: string; name: string }>;
    recipes: Array<{ id: string; name: string }>;
    shoppingLists: Array<{ id: string; name: string }>;
    rewards: Array<{ id: string; name: string }>;
    mealPlans: Array<{ id: string; name: string }>;
  };
  
  // Metadatos
  downgradedAt: Timestamp;
  reason: 'expiration' | 'cancellation' | 'manual';
  canRestore: boolean;
  restoredAt?: Timestamp;
}

export interface DowngradeRecommendations {
  groupId?: string;
  families?: string[]; // Recomendaciones para grupos/familias
  members?: string[]; // Recomendaciones para miembros
  activityIds: string[];
  checklistIds: string[];
  recipeIds: string[];
  shoppingListId?: string;
  reason: string; // Por qué se recomiendan estos
}

/**
 * Obtiene el conteo actual de items por feature para una familia
 */
export async function getCurrentCounts(familyId: string): Promise<Record<string, number>> {
  console.log('📊 Contando items para familyId:', familyId);
  const counts: Record<string, number> = {};
  
  // ⭐ Contar cada colección individualmente, continuando si alguna falla
  try {
    const membersSnap = await getDocs(
      query(collection(db, 'members'), where('familyId', '==', familyId))
    );
    counts.members = membersSnap.size;
    console.log('👤 Miembros encontrados:', counts.members);
  } catch (error) {
    console.warn('⚠️ Error contando members:', error);
    counts.members = 0;
  }
  
  try {
    const activitiesSnap = await getDocs(
      query(collection(db, 'activities'), where('familyId', '==', familyId))
    );
    counts.activities = activitiesSnap.size;
    console.log('🏃 Actividades encontradas:', counts.activities);
  } catch (error) {
    console.warn('⚠️ Error contando activities:', error);
    counts.activities = 0;
  }
  
  try {
    const checklistsSnap = await getDocs(
      query(collection(db, 'checklist'), where('familyId', '==', familyId))
    );
    counts.checklists = checklistsSnap.size;
    console.log('📋 Checklists encontradas:', counts.checklists);
  } catch (error) {
    console.warn('⚠️ Error contando checklists:', error);
    counts.checklists = 0;
  }
  
  try {
    const recipesSnap = await getDocs(
      query(collection(db, 'recipes'), where('familyId', '==', familyId))
    );
    counts.recipes = recipesSnap.size;
    console.log('🍳 Recetas encontradas:', counts.recipes);
  } catch (error) {
    console.warn('⚠️ Error contando recipes:', error);
    counts.recipes = 0;
  }
  
  try {
    const shoppingListsSnap = await getDocs(
      query(collection(db, 'shopping_lists'), where('familyId', '==', familyId))
    );
    counts.shoppingLists = shoppingListsSnap.size;
    console.log('🛒 Listas de compras encontradas:', counts.shoppingLists);
  } catch (error) {
    console.warn('⚠️ Error contando shopping_lists (permisos?):', error);
    counts.shoppingLists = 0;
  }
  
  try {
    const rewardsSnap = await getDocs(
      query(collection(db, 'rewards'), where('familyId', '==', familyId))
    );
    counts.rewards = rewardsSnap.size;
    console.log('🎁 Recompensas encontradas:', counts.rewards);
  } catch (error) {
    console.warn('⚠️ Error contando rewards (permisos?):', error);
    counts.rewards = 0;
  }
  
  console.log('✅ Conteos totales:', counts);
  return counts;
}

/**
 * Obtiene recomendaciones inteligentes de qué mantener
 * Basado en uso reciente y frecuencia de completado
 */
export async function getDowngradeRecommendations(
  familyId: string,
  limits: { activities: number; checklists: number; recipes: number }
): Promise<DowngradeRecommendations> {
  try {
    const recommendations: DowngradeRecommendations = {
      activityIds: [],
      checklistIds: [],
      recipeIds: [],
      reason: 'Basado en uso reciente y frecuencia',
    };
    
    // Recomendar actividades más usadas (últimos 30 días)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const activitiesSnap = await getDocs(
      query(
        collection(db, 'activities'),
        where('familyId', '==', familyId),
        where('active', '==', true)
      )
    );
    
    // Ordenar por uso y tomar las top N
    const activities = activitiesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as any[];
    
    // Ordenar por puntos o uso (puedes mejorar esto con datos de daily_log)
    activities.sort((a, b) => (b.points || 0) - (a.points || 0));
    recommendations.activityIds = activities.slice(0, limits.activities).map(a => a.id);
    
    // Recomendar checklists más recientes (solo si el límite es válido)
    if (limits.checklists > 0) {
      const checklistsSnap = await getDocs(
        query(
          collection(db, 'checklist'),
          where('familyId', '==', familyId),
          orderBy('createdAt', 'desc'),
          firestoreLimit(limits.checklists)
        )
      );
      recommendations.checklistIds = checklistsSnap.docs.map(doc => doc.id);
    }
    
    // Recomendar recetas más recientes (solo si el límite es válido)
    if (limits.recipes > 0) {
      const recipesSnap = await getDocs(
        query(
          collection(db, 'recipes'),
          where('familyId', '==', familyId),
          orderBy('createdAt', 'desc'),
          firestoreLimit(limits.recipes)
        )
      );
      recommendations.recipeIds = recipesSnap.docs.map(doc => doc.id);
    }
    
    // Recomendar lista de compras más reciente
    const shoppingListsSnap = await getDocs(
      query(
        collection(db, 'shopping_lists'),
        where('familyId', '==', familyId),
        orderBy('updatedAt', 'desc'),
        firestoreLimit(1)
      )
    );
    if (!shoppingListsSnap.empty) {
      recommendations.shoppingListId = shoppingListsSnap.docs[0].id;
    }
    
    return recommendations;
  } catch (error) {
    console.error('Error obteniendo recomendaciones:', error);
    return {
      activityIds: [],
      checklistIds: [],
      recipeIds: [],
      reason: 'Error al obtener recomendaciones',
    };
  }
}

/**
 * Crea un snapshot del estado actual antes del downgrade
 */
export async function createDowngradeSnapshot(
  userId: string,
  familyId: string,
  previousPlanId: string,
  selections: DowngradeSelections
): Promise<string> {
  console.log('📸 Iniciando creación de snapshot...', { userId, familyId, previousPlanId, selections });
  
  try {
    // Obtener info del plan anterior
    const previousPlanDoc = await getDoc(doc(db, 'subscriptionPlans', previousPlanId));
    const previousPlanData = previousPlanDoc.data();
    console.log('📋 Plan anterior obtenido:', previousPlanData?.name);
    
    // Obtener contenido pausado
    const pausedContent = {
      groups: [],
      members: [],
      activities: [],
      checklists: [],
      recipes: [],
      shoppingLists: [],
      rewards: [],
      mealPlans: [],
    } as DowngradeSnapshot['pausedContent'];
    
    // Obtener actividades que se pausarán
    if (selections.activeActivityIds) {
      const allActivitiesSnap = await getDocs(
        query(collection(db, 'activities'), where('familyId', '==', familyId), where('active', '==', true))
      );
      pausedContent.activities = allActivitiesSnap.docs
        .filter(doc => !selections.activeActivityIds!.includes(doc.id))
        .map(doc => ({ id: doc.id, name: doc.data().name || 'Sin nombre' }));
    }
    
    // Obtener checklists que se pausarán
    if (selections.activeChecklistIds) {
      const allChecklistsSnap = await getDocs(
        query(collection(db, 'checklist'), where('familyId', '==', familyId))
      );
      pausedContent.checklists = allChecklistsSnap.docs
        .filter(doc => !selections.activeChecklistIds!.includes(doc.id))
        .map(doc => ({ id: doc.id, name: doc.data().name || 'Sin nombre' }));
    }
    
    // Obtener recetas que se pausarán
    if (selections.activeRecipeIds) {
      const allRecipesSnap = await getDocs(
        query(collection(db, 'recipes'), where('familyId', '==', familyId))
      );
      pausedContent.recipes = allRecipesSnap.docs
        .filter(doc => !selections.activeRecipeIds!.includes(doc.id))
        .map(doc => ({ id: doc.id, name: doc.data().name || 'Sin nombre' }));
    }
    
    // Obtener listas de compras que se pausarán
    if (selections.activeShoppingListId) {
      const allShoppingListsSnap = await getDocs(
        query(collection(db, 'shopping_lists'), where('familyId', '==', familyId))
      );
      pausedContent.shoppingLists = allShoppingListsSnap.docs
        .filter(doc => doc.id !== selections.activeShoppingListId)
        .map(doc => ({ id: doc.id, name: doc.data().name || 'Sin nombre' }));
    }
    
    // Obtener todas las recompensas (se pausan todas en plan free)
    const allRewardsSnap = await getDocs(
      query(collection(db, 'rewards'), where('familyId', '==', familyId))
    );
    pausedContent.rewards = allRewardsSnap.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name || 'Sin nombre',
    }));
    
    // Crear snapshot
    const snapshot: DowngradeSnapshot = {
      userId,
      familyId,
      previousPlan: {
        id: previousPlanId,
        name: previousPlanData?.name || 'Unknown',
        expiresAt: Timestamp.now(),
      },
      selections,
      pausedContent,
      downgradedAt: Timestamp.now(),
      reason: 'expiration',
      canRestore: true,
    };
    
    console.log('💾 Guardando snapshot en Firestore...', {
      activitiesPausadas: pausedContent.activities.length,
      checklistsPausadas: pausedContent.checklists.length,
      recetasPausadas: pausedContent.recipes.length,
      listasComprasPausadas: pausedContent.shoppingLists.length,
      recompensasPausadas: pausedContent.rewards.length,
    });
    
    const snapshotRef = doc(collection(db, 'downgrade_snapshots'));
    await setDoc(snapshotRef, snapshot);
    
    console.log('✅ Snapshot guardado exitosamente con ID:', snapshotRef.id);
    
    return snapshotRef.id;
  } catch (error) {
    console.error('❌ Error creando snapshot:', error);
    throw error;
  }
}

/**
 * Pausa contenido no seleccionado
 */
export async function pauseUnselectedContent(
  familyId: string,
  selections: DowngradeSelections
): Promise<void> {
  console.log('⏸️ Iniciando pausa de contenido...', { familyId, selections });
  
  try {
    const batch = writeBatch(db);
    let updateCount = 0;
    
    // Pausar actividades no seleccionadas
    if (selections.activeActivityIds) {
      const activitiesSnap = await getDocs(
        query(collection(db, 'activities'), where('familyId', '==', familyId), where('active', '==', true))
      );
      
      console.log(`🏃 Total actividades: ${activitiesSnap.size}, Seleccionadas: ${selections.activeActivityIds.length}`);
      
      activitiesSnap.docs.forEach(docSnap => {
        if (!selections.activeActivityIds!.includes(docSnap.id)) {
          console.log(`  ⏸️ Pausando actividad: ${docSnap.data().name} (${docSnap.id})`);
          batch.update(docSnap.ref, {
            active: false,
            pausedByDowngrade: true,
            pausedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          updateCount++;
        } else {
          console.log(`  ✅ Manteniendo actividad: ${docSnap.data().name} (${docSnap.id})`);
        }
      });
    }
    
    // Pausar checklists no seleccionados
    if (selections.activeChecklistIds) {
      const checklistsSnap = await getDocs(
        query(collection(db, 'checklist'), where('familyId', '==', familyId))
      );
      
      console.log(`📋 Total checklists: ${checklistsSnap.size}, Seleccionadas: ${selections.activeChecklistIds.length}`);
      
      checklistsSnap.docs.forEach(docSnap => {
        if (!selections.activeChecklistIds!.includes(docSnap.id)) {
          console.log(`  ⏸️ Pausando checklist: ${docSnap.data().name} (${docSnap.id})`);
          batch.update(docSnap.ref, {
            active: false,
            pausedByDowngrade: true,
            pausedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          updateCount++;
        } else {
          console.log(`  ✅ Manteniendo checklist: ${docSnap.data().name} (${docSnap.id})`);
        }
      });
    }
    
    // Pausar recetas no seleccionadas
    if (selections.activeRecipeIds) {
      const recipesSnap = await getDocs(
        query(collection(db, 'recipes'), where('familyId', '==', familyId))
      );
      
      recipesSnap.docs.forEach(docSnap => {
        if (!selections.activeRecipeIds!.includes(docSnap.id)) {
          batch.update(docSnap.ref, {
            active: false,
            pausedByDowngrade: true,
            pausedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          updateCount++;
        }
      });
    }
    
    // Pausar listas de compras no seleccionadas
    if (selections.activeShoppingListId) {
      const shoppingListsSnap = await getDocs(
        query(collection(db, 'shopping_lists'), where('familyId', '==', familyId))
      );
      
      shoppingListsSnap.docs.forEach(docSnap => {
        if (docSnap.id !== selections.activeShoppingListId) {
          batch.update(docSnap.ref, {
            active: false,
            pausedByDowngrade: true,
            pausedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          updateCount++;
        }
      });
    }
    
    // Pausar miembros no seleccionados
    if (selections.activeMemberIds) {
      const membersSnap = await getDocs(
        query(collection(db, 'members'), where('familyId', '==', familyId))
      );
      
      console.log(`👤 Total miembros: ${membersSnap.size}, Seleccionados: ${selections.activeMemberIds.length}`);
      
      membersSnap.docs.forEach(docSnap => {
        if (!selections.activeMemberIds!.includes(docSnap.id)) {
          console.log(`  ⏸️ Pausando miembro: ${docSnap.data().name} (${docSnap.id})`);
          batch.update(docSnap.ref, {
            active: false,
            pausedByDowngrade: true,
            pausedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          updateCount++;
        } else {
          console.log(`  ✅ Manteniendo miembro: ${docSnap.data().name} (${docSnap.id})`);
        }
      });
    }
    
    // Pausar todas las recompensas (bloqueadas en plan free)
    const rewardsSnap = await getDocs(
      query(collection(db, 'rewards'), where('familyId', '==', familyId))
    );
    
    rewardsSnap.docs.forEach(docSnap => {
      batch.update(docSnap.ref, {
        active: false,
        pausedByDowngrade: true,
        pausedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    });
    
    // Si hay más de 500 updates, dividir en múltiples batches
    if (updateCount > 0) {
      await batch.commit();
      console.log(`✅ ${updateCount} items pausados correctamente`);
    }
  } catch (error) {
    console.error('Error pausando contenido:', error);
    throw error;
  }
}

/**
 * Restaura contenido pausado al renovar plan
 */
export async function restoreFromDowngrade(
  snapshotId: string
): Promise<void> {
  try {
    const snapshotDoc = await getDoc(doc(db, 'downgrade_snapshots', snapshotId));
    
    if (!snapshotDoc.exists()) {
      throw new Error('Snapshot no encontrado');
    }
    
    const snapshot = snapshotDoc.data() as DowngradeSnapshot;
    const batch = writeBatch(db);
    let updateCount = 0;
    
    // Restaurar actividades
    for (const activity of snapshot.pausedContent.activities) {
      const activityRef = doc(db, 'activities', activity.id);
      batch.update(activityRef, {
        active: true,
        pausedByDowngrade: false,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    }
    
    // Restaurar checklists
    for (const checklist of snapshot.pausedContent.checklists) {
      const checklistRef = doc(db, 'checklist', checklist.id);
      batch.update(checklistRef, {
        active: true,
        pausedByDowngrade: false,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    }
    
    // Restaurar recetas
    for (const recipe of snapshot.pausedContent.recipes) {
      const recipeRef = doc(db, 'recipes', recipe.id);
      batch.update(recipeRef, {
        active: true,
        pausedByDowngrade: false,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    }
    
    // Restaurar miembros
    for (const member of snapshot.pausedContent.members) {
      const memberRef = doc(db, 'members', member.id);
      batch.update(memberRef, {
        active: true,
        pausedByDowngrade: false,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    }
    
    // Restaurar listas de compras
    for (const shoppingList of snapshot.pausedContent.shoppingLists) {
      const shoppingListRef = doc(db, 'shopping_lists', shoppingList.id);
      batch.update(shoppingListRef, {
        active: true,
        pausedByDowngrade: false,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    }
    
    // Restaurar recompensas
    for (const reward of snapshot.pausedContent.rewards) {
      const rewardRef = doc(db, 'rewards', reward.id);
      batch.update(rewardRef, {
        active: true,
        pausedByDowngrade: false,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      updateCount++;
    }
    
    // Actualizar snapshot como restaurado
    batch.update(snapshotDoc.ref, {
      restoredAt: serverTimestamp(),
      canRestore: false,
    });
    
    if (updateCount > 0) {
      await batch.commit();
      console.log(`✅ ${updateCount} items restaurados correctamente`);
      
      // ⭐ FASE 2: Actualizar metadata para detectar cambios
      const familyId = snapshot.familyId;
      if (familyId) {
        await updateFamilyMetadata(familyId);
      }
    }
  } catch (error) {
    console.error('Error restaurando contenido:', error);
    throw error;
  }
}

/**
 * Obtiene el snapshot más reciente de downgrade para un usuario
 */
export async function getLatestDowngradeSnapshot(
  userId: string
): Promise<DowngradeSnapshot | null> {
  try {
    const snapshotsSnap = await getDocs(
      query(
        collection(db, 'downgrade_snapshots'),
        where('userId', '==', userId),
        where('canRestore', '==', true),
        orderBy('downgradedAt', 'desc'),
        firestoreLimit(1)
      )
    );
    
    if (snapshotsSnap.empty) return null;
    
    return {
      id: snapshotsSnap.docs[0].id,
      ...snapshotsSnap.docs[0].data(),
    } as DowngradeSnapshot;
  } catch (error) {
    console.error('Error obteniendo snapshot:', error);
    return null;
  }
}
